import type { TaskPlanDraftFormValues } from '../schemas';

export interface TaskPlanSelectionSummary {
  selectedCount: number;
  totalEstimatedMinutes: number | null;
  hasUnestimated: boolean;
  /** How many selected rows lack an estimate — the number the summary reports. */
  unestimatedCount: number;
}

/**
 * Computes selection summary for a task plan draft list.
 *
 * - `selectedCount`: how many rows the user checked.
 * - `totalEstimatedMinutes`: sum of estimates for selected rows that HAVE one;
 *   `null` when no selected row has an estimate.
 * - `hasUnestimated`: true when at least one selected row lacks an estimate.
 *   Unestimated rows are excluded from the sum rather than counted as zero (D4).
 * - `unestimatedCount`: how many of them. The summary used to derive this from the
 *   selected total, which reported the wrong number whenever more than one row
 *   lacked an estimate.
 */
export function computeTaskPlanSelection(
  drafts: TaskPlanDraftFormValues[],
): TaskPlanSelectionSummary {
  const selected = drafts.filter((d) => d.selected);

  let sum = 0;
  let hasEstimate = false;
  let unestimatedCount = 0;

  for (const row of selected) {
    if (row.estimatedMinutes != null) {
      sum += row.estimatedMinutes;
      hasEstimate = true;
    } else {
      unestimatedCount += 1;
    }
  }

  return {
    selectedCount: selected.length,
    totalEstimatedMinutes: hasEstimate ? sum : null,
    hasUnestimated: unestimatedCount > 0,
    unestimatedCount,
  };
}
